import type { RuleOverrides } from '../../data/ruleRegistry';
import { getRuleValue } from '../../data/ruleRegistry';
import type { HeaterType, PermitRequirementsResult, PermitWallLabelKey, PermitWarningEntry, SaunaSiting } from '../../engines/types';
import type { Market } from '../location/types';
import { getT } from '../../i18n';

interface PermitInput {
  areaSqm: number;
  heaterType: HeaterType;
  glazingStyle?: 'standard' | 'panorama';
  glassWalls?: PermitGlassWall[];
  panoramaGlassUValue?: number;
  siting?: SaunaSiting;
}

interface PermitGlassWall {
  wall: 'front' | 'back' | 'left' | 'right';
  wallAreaSqm: number;
  glassAreaSqm: number;
}

/**
 * Wall enum → dictionary key. The warning templates interpolate `{wall}`, and
 * args are plain `.replace` scalars that can never resolve a nested key — so the
 * label is resolved here, against the caller's market, before interpolation.
 */
const WALL_LABEL_KEYS: Record<PermitGlassWall['wall'], PermitWallLabelKey> = {
  front: 'domain_permit_wallFront',
  back: 'domain_permit_wallBack',
  left: 'domain_permit_wallLeft',
  right: 'domain_permit_wallRight',
};

interface PermitTier {
  permitLabel: string;
  status: PermitRequirementsResult['status'];
  messages: string[];
  checklist: string[];
}

export function calculatePermitRequirements({
  areaSqm,
  heaterType,
  glazingStyle = 'standard',
  glassWalls = [],
  panoramaGlassUValue,
  siting,
}: PermitInput, market: Market = 'se', overrides: RuleOverrides = {}): PermitRequirementsResult {
  const t = getT(market);
  const roundedArea = Number(areaSqm.toFixed(1));
  const requiresFireplaceNotification = heaterType === 'wood';

  if (siting === 'indoor') {
    return buildIndoorResult(roundedArea, requiresFireplaceNotification, t);
  }
  const warningEntries: PermitWarningEntry[] = [];

  const tier = market === 'se'
    ? buildSwedishTier(roundedArea, overrides, t)
    : buildLegacyTier(roundedArea, t);
  let { status } = tier;
  const messages = [...tier.messages];

  messages.push(t.domain_permit_msgBoundary);
  if (requiresFireplaceNotification) {
    messages.push(t.domain_permit_msgFireplace);
  }

  const panoramaGlass = glazingStyle === 'panorama'
    ? buildPanoramaGlassResult(glassWalls, panoramaGlassUValue, overrides, warningEntries, t)
    : undefined;
  for (const entry of warningEntries) {
    messages.push(entry.message);
    if (entry.severity === 'critical') {
      status = 'critical';
    } else if (status === 'ok') {
      status = 'warning';
    }
  }

  return {
    permitLabel: tier.permitLabel,
    status,
    areaSqm: roundedArea,
    requiresFireplaceNotification,
    messages,
    checklist: [
      ...tier.checklist,
      t.domain_permit_checkBoundary,
      requiresFireplaceNotification
        ? t.domain_permit_checkFireplace
        : t.domain_permit_checkElectrical,
    ],
    ...(warningEntries.length > 0 ? { warningEntries } : {}),
    ...(panoramaGlass ? { panoramaGlass } : {}),
  };
}

/**
 * A sauna built as a room inside an existing building (issue 156).
 *
 * It adds no building area, so none of the komplementbyggnad tiers, the 4.5 m
 * boundary rule or strandskydd apply. What remains: an eldstad is still
 * anmälningspliktig wherever it stands (PBF 6 kap. 5 § p. 3 — installation of
 * an eldstad or rökkanal), and the heater's supply is a fixed installation
 * for a behörig elinstallatör (elsäkerhetslagen 2016:732).
 */
function buildIndoorResult(
  roundedArea: number,
  requiresFireplaceNotification: boolean,
  t: ReturnType<typeof getT>,
): PermitRequirementsResult {
  return {
    notApplicable: true,
    permitLabel: t.domain_permit_indoorLabel,
    status: 'ok',
    areaSqm: roundedArea,
    requiresFireplaceNotification,
    messages: [
      t.domain_permit_indoorMsg,
      ...(requiresFireplaceNotification ? [t.domain_permit_msgFireplace] : []),
    ],
    checklist: [
      t.domain_permit_indoorCheckStructure,
      requiresFireplaceNotification
        ? t.domain_permit_checkFireplace
        : t.domain_permit_checkElectrical,
    ],
  };
}

/**
 * Swedish tiers per the PBL reform of 1 December 2025: friggebod and
 * attefallshus are merged into "bygglovsbefriad komplementbyggnad".
 * The building itself needs neither bygglov, anmälan nor startbesked up to
 * 30 m² (inside detaljplan, 45 m² combined per plot) or 50 m² (outside
 * detaljplan, 65 m² combined). Eldstadsanmälan, the 4.5 m boundary rule and
 * strandskydd still apply. The app does not know the plot's detaljplan
 * status, so the 30–50 m² band is reported as a warning.
 */
function buildSwedishTier(
  roundedArea: number,
  overrides: RuleOverrides,
  t: ReturnType<typeof getT>,
): PermitTier {
  const maxDetaljplan = getRuleValue<number>('permit.se.exemptMaxAreaDetaljplanM2', overrides);
  const totalDetaljplan = getRuleValue<number>('permit.se.exemptTotalAreaDetaljplanM2', overrides);
  const maxOutside = getRuleValue<number>('permit.se.exemptMaxAreaOutsideDetaljplanM2', overrides);
  const totalOutside = getRuleValue<number>('permit.se.exemptTotalAreaOutsideDetaljplanM2', overrides);
  const maxRidge = getRuleValue<number>('permit.se.exemptMaxRidgeHeightM', overrides);
  const args = { maxDetaljplan, totalDetaljplan, maxOutside, totalOutside, maxRidge };

  const exemptChecklist = [
    formatTemplate(t.domain_permit_se_checkArea, args),
    formatTemplate(t.domain_permit_se_checkTotalArea, args),
    formatTemplate(t.domain_permit_se_checkRidgeHeight, args),
    t.domain_permit_se_checkStrandskydd,
  ];

  if (roundedArea <= maxDetaljplan) {
    return {
      permitLabel: t.domain_permit_komplementbyggnad,
      status: 'ok',
      messages: [
        formatTemplate(t.domain_permit_se_msgExempt, args),
        formatTemplate(t.domain_permit_se_msgTotalArea, args),
        t.domain_permit_se_msgStrandskydd,
      ],
      checklist: exemptChecklist,
    };
  }

  if (roundedArea <= maxOutside) {
    return {
      permitLabel: t.domain_permit_komplementbyggnadOutsideDetaljplan,
      status: 'warning',
      messages: [
        formatTemplate(t.domain_permit_se_msgExemptOutsideDetaljplanOnly, args),
        formatTemplate(t.domain_permit_se_msgTotalArea, args),
        t.domain_permit_se_msgStrandskydd,
      ],
      checklist: exemptChecklist,
    };
  }

  return {
    permitLabel: t.domain_permit_bygglov,
    status: 'critical',
    messages: [formatTemplate(t.domain_permit_se_msgOverMax, args)],
    checklist: [formatTemplate(t.domain_permit_se_checkArea, args)],
  };
}

/** Generic intl tiers — intentionally untouched by the Swedish PBL reform. */
function buildLegacyTier(
  roundedArea: number,
  t: ReturnType<typeof getT>,
): PermitTier {
  if (roundedArea < 15) {
    return {
      permitLabel: t.domain_permit_friggebod,
      status: 'ok',
      messages: [t.domain_permit_msgUnder15],
      checklist: [t.domain_permit_checkArea],
    };
  }
  if (roundedArea <= 30) {
    return {
      permitLabel: t.domain_permit_attefall,
      status: 'warning',
      messages: [t.domain_permit_msg15to30],
      checklist: [t.domain_permit_checkArea],
    };
  }
  return {
    permitLabel: t.domain_permit_bygglov,
    status: 'critical',
    messages: [t.domain_permit_msgOver30],
    checklist: [t.domain_permit_checkArea],
  };
}

function buildPanoramaGlassResult(
  glassWalls: PermitGlassWall[],
  panoramaGlassUValue: number | undefined,
  overrides: RuleOverrides,
  warningEntries: PermitWarningEntry[],
  t: ReturnType<typeof getT>,
): NonNullable<PermitRequirementsResult['panoramaGlass']> {
  const maxPercentPerWall = getRuleValue<number>('glass.maxPercentPerWall.panorama', overrides);
  const safetyGlassThresholdSqm = getRuleValue<number>('glass.panorama.safetyGlassThresholdM2', overrides);
  const uValue = panoramaGlassUValue ?? getRuleValue<number>('glass.panorama.uValueDefault', overrides);
  const wallAssessments = glassWalls.map((wall) => {
    const glassPercent = wall.wallAreaSqm > 0 ? wall.glassAreaSqm / wall.wallAreaSqm : 0;
    const roundedPercent = Math.round(glassPercent * 100);
    const roundedMaxPercent = Math.round(maxPercentPerWall * 100);
    const requiresSafetyGlass = wall.glassAreaSqm >= safetyGlassThresholdSqm;
    const wallLabelKey = WALL_LABEL_KEYS[wall.wall];
    const wallLabel = t[wallLabelKey];

    if (glassPercent > maxPercentPerWall) {
      const args = {
        wall: wall.wall,
        percent: roundedPercent,
        maxPercent: roundedMaxPercent,
      };
      warningEntries.push({
        warningKey: 'domain_permit_panorama_glassPercentExceeded',
        severity: 'critical',
        message: formatTemplate(t.domain_permit_panorama_glassPercentExceeded, {
          ...args,
          wall: wallLabel,
        }),
        args,
        wallLabelKey,
      });
    }

    if (requiresSafetyGlass) {
      const args = {
        wall: wall.wall,
        glassAreaSqm: round1(wall.glassAreaSqm),
        thresholdSqm: safetyGlassThresholdSqm,
      };
      warningEntries.push({
        warningKey: 'domain_permit_panorama_safetyGlassRequired',
        severity: 'warning',
        message: formatTemplate(t.domain_permit_panorama_safetyGlassRequired, {
          ...args,
          wall: wallLabel,
        }),
        args,
        wallLabelKey,
      });
    }

    return {
      wall: wall.wall,
      wallAreaSqm: round1(wall.wallAreaSqm),
      glassAreaSqm: round1(wall.glassAreaSqm),
      glassPercent: round1(glassPercent),
      maxPercent: maxPercentPerWall,
      requiresSafetyGlass,
    };
  });

  return {
    maxPercentPerWall,
    safetyGlassThresholdSqm,
    uValue,
    wallAssessments,
  };
}

function round1(value: number): number {
  return Number(value.toFixed(1));
}

function formatTemplate(template: string, args: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(args[key] ?? ''));
}
